'use client';

import { useMemo } from 'react';
import { useContextSelector } from 'use-context-selector';
import {
  MountedProjectsContext,
  type MountedProjectsContextValue,
} from '../contexts/MountedProjectsContextProvider';

function requireContext(value: MountedProjectsContextValue | null) {
  if (!value) throw new Error('MountedProjectsContextProvider not mounted');
  return value;
}

export function useMountedProjects() {
  return useContextSelector(
    MountedProjectsContext,
    (value) => requireContext(value).state
  );
}

/**
 * Mount state for one project. Unlike the other mounted-project hooks, this
 * does not require `MountedProjectsContextProvider`: without it the project
 * reads as unmounted with an `unavailable` status, so shared UI such as
 * project cards can render outside the main app (for example in Storybook).
 */
export function useIsProjectMounted(projectId: string | null | undefined) {
  const isMounted = useContextSelector(
    MountedProjectsContext,
    (value) =>
      projectId != null && Boolean(value?.state.projectIds.includes(projectId))
  );
  const loading = useContextSelector(
    MountedProjectsContext,
    (value) => value?.state.loading ?? false
  );
  const error = useContextSelector(
    MountedProjectsContext,
    (value) => value?.state.error ?? null
  );
  const status = useContextSelector(
    MountedProjectsContext,
    (value) => value?.state.status ?? 'unavailable'
  );
  return useMemo(
    () => ({ isMounted, loading, error, status }),
    [isMounted, loading, error, status]
  );
}

const unavailableMountActions: MountedProjectsContextValue['actions'] = {
  mountProject: () =>
    Promise.reject(new Error('MountedProjectsContextProvider not mounted')),
  ejectProject: () =>
    Promise.reject(new Error('MountedProjectsContextProvider not mounted')),
};

/**
 * Mount and eject actions. Without `MountedProjectsContextProvider` the
 * actions reject instead of throwing on render, matching
 * `useIsProjectMounted`.
 */
export function useProjectMountActions() {
  return useContextSelector(
    MountedProjectsContext,
    (value) => value?.actions ?? unavailableMountActions
  );
}
